import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../../../contexts/AuthContext';
import {
  startOrgImport,
  subscribeToOrgImport,
  approveOrgImport,
  validateImportFile,
} from '../../../services/orgImportService';
import { emitOnboardingStep } from '../../../onboarding/onboardingEvents';
import { normalizeProposal } from '../utils/importStructure';

/**
 * Compact, non-overwhelming presentation of the AI's judgment calls.
 * Shows the first few; the rest sit behind a "show more" toggle. Long
 * explanations are trimmed to their first sentence with the detail on demand.
 */
function AmbiguityList({ items }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null); // index of item showing full text
  const PREVIEW_COUNT = 3;

  const firstSentence = (s) => {
    const m = String(s).match(/^.*?[.!?](\s|$)/);
    return m ? m[0].trim() : s;
  };

  const visible = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const hidden = items.length - PREVIEW_COUNT;

  return (
    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        The AI made {items.length} judgment call{items.length === 1 ? '' : 's'} you may
        want to double-check:
      </Typography>
      {visible.map((a, i) => {
        const short = firstSentence(a);
        const hasMore = short.length < String(a).length;
        return (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.75, pl: 0.5 }}>
            • {detail === i ? a : short}{' '}
            {hasMore && (
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer' }}
                onClick={() => setDetail(detail === i ? null : i)}
              >
                {detail === i ? 'less' : 'why?'}
              </Typography>
            )}
          </Typography>
        );
      })}
      {hidden > 0 && (
        <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ mt: 0.5 }}>
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </Button>
      )}
    </Box>
  );
}

/**
 * "Import Organization" — upload an org chart (PDF, image, spreadsheet, doc),
 * the AI proposes a sites → departments → teams structure, the user reviews and
 * edits it, and only on Approve is the organization created.
 */
export default function OrgImportDialog({ open, onClose, onCreated, existingOrganization = null }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  // phase: 'pick' | 'processing' | 'review' | 'done'
  const [phase, setPhase] = useState('pick');
  const [error, setError] = useState(null);
  const [importDoc, setImportDoc] = useState(null);
  const [importId, setImportId] = useState(null);
  const [structure, setStructure] = useState(null); // editable copy
  const [approving, setApproving] = useState(false);
  const [includePeople, setIncludePeople] = useState(false); // P4-15 "Names found?"
  const [editing, setEditing] = useState(null); // { kind, id } being renamed

  // Subscribe to the import doc while processing
  useEffect(() => {
    if (!importId) return undefined;
    const unsub = subscribeToOrgImport(importId, (d) => {
      setImportDoc(d);
      if (d?.status === 'ready_for_review' && d.proposedStructure) {
        setStructure(normalizeProposal(JSON.parse(JSON.stringify(d.proposedStructure))));
        setPhase('review');
      }
      if (d?.status === 'failed') {
        setError(d.userMessage || 'Something went wrong. Please try again.');
        setPhase('pick');
      }
    });
    return unsub;
  }, [importId]);

  const reset = () => {
    setPhase('pick');
    setError(null);
    setImportDoc(null);
    setImportId(null);
    setStructure(null);
    setApproving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    const check = validateImportFile(file);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    try {
      setPhase('processing');
      const id = await startOrgImport({ file, orgNameHint: existingOrganization?.name || null, user });
      setImportId(id);
    } catch (e) {
      setError(e.message);
      setPhase('pick');
    }
  };

  // ---- review-phase edits (deletes cascade) ----
  const removeSite = (siteId) => {
    setStructure((s) => {
      const departments = s.departments.filter((d) => d.siteId !== siteId);
      const keptDeptIds = new Set(departments.map((d) => d.id));
      return {
        ...s,
        sites: s.sites.filter((x) => x.id !== siteId),
        departments,
        teams: s.teams.filter((t) => keptDeptIds.has(t.departmentId)),
      };
    });
  };
  const removeDept = (deptId) => {
    setStructure((s) => ({
      ...s,
      departments: s.departments.filter((d) => d.id !== deptId),
      teams: s.teams.filter((t) => t.departmentId !== deptId),
    }));
  };
  const removeTeam = (teamId) => {
    setStructure((s) => ({ ...s, teams: s.teams.filter((t) => t.id !== teamId) }));
  };
  // P4-15 visual confirmation map: rename any unit in place before Confirm.
  const rename = (kind, id, name) => {
    setStructure((s) => ({ ...s, [kind]: s[kind].map((u) => (u.id === id ? { ...u, name } : u)) }));
  };
  const removeDivision = (divisionId) => {
    setStructure((s) => ({ ...s, divisions: s.divisions.filter((d) => d.id !== divisionId), sites: s.sites.map((x) => (x.divisionId === divisionId ? { ...x, divisionId: null } : x)) }));
  };
  const RenameField = ({ kind, unit, variant = 'body2', bold = true }) =>
    editing?.kind === kind && editing?.id === unit.id ? (
      <TextField
        size="small"
        autoFocus
        value={unit.name}
        onChange={(e) => rename(kind, unit.id, e.target.value)}
        onBlur={() => setEditing(null)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
        sx={{ minWidth: 220 }}
        inputProps={{ 'aria-label': `Rename ${unit.name}` }}
      />
    ) : (
      <Typography
        variant={variant}
        fontWeight={bold ? 700 : 500}
        component="span"
        onClick={() => setEditing({ kind, id: unit.id })}
        sx={{ cursor: 'text', borderBottom: '1px dashed', borderColor: 'divider', '&:hover': { borderColor: 'primary.main' } }}
        title="Click to rename"
      >
        {unit.name}
      </Typography>
    );

  const handleApprove = async () => {
    setApproving(true);
    setError(null);
    try {
      const org = await approveOrgImport({ importId, structure, user, existingOrganization, includePeople });
      setPhase('done');
      // Onboarding: approving an import = "bring in your real organization".
      emitOnboardingStep('import-your-org');
      if (onCreated) onCreated(org);
    } catch (e) {
      setError(e.message);
    } finally {
      setApproving(false);
    }
  };

  const counts = structure
    ? { s: structure.sites.length, d: structure.departments.length, t: structure.teams.length }
    : null;

  return (
    <Dialog open={open} onClose={phase === 'processing' ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="primary" fontSize="small" />
        {existingOrganization ? `Import structure into ${existingOrganization.name}` : 'Import Organization'}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {phase === 'pick' && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFile(e.dataTransfer.files?.[0]);
            }}
          >
            <UploadFileIcon sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Upload your org chart
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drop a file here or click to browse. PDF, image (a photo of a wall chart or
              whiteboard will work), Excel/CSV roster, Word doc, or plain text. REPSit will read
              your upload and propose your sites, departments, and teams. You review everything
              before anything is created.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Using PowerPoint or Visio? Export as PDF first for best results. Rosters with
              employee names are fine and will expedite the organizational setup process. Remove
              names first if your company policy requires it.
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,.docx,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </Box>
        )}

        {phase === 'processing' && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {importDoc?.status === 'parsing' ? 'Reading your org chart…' : 'Uploading…'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The AI is identifying your sites, departments, and teams. This usually takes
              under a minute.
            </Typography>
            <LinearProgress sx={{ mt: 3, maxWidth: 360, mx: 'auto' }} />
          </Box>
        )}

        {phase === 'review' && structure && (
          <Box>
            <Alert severity="info" icon={<AutoAwesomeIcon fontSize="inherit" />} sx={{ mb: 2 }}>
              Here's what we found — <strong>{counts.s} sites, {counts.d} departments, {counts.t} NWTs</strong>
              {structure.divisions?.length ? `, ${structure.divisions.length} division${structure.divisions.length === 1 ? '' : 's'}` : ''}
              {importDoc?.confidence ? ` (confidence: ${importDoc.confidence})` : ''}. This is your confirmation map:
              click a name to rename it, remove anything that's wrong, then confirm. Nothing is {existingOrganization ? 'added' : 'created'} until you do.
            </Alert>

            {structure.people?.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>Names found — {structure.people.length} {structure.people.length === 1 ? 'person' : 'people'} in the chart.</Typography>
                <RadioGroup value={includePeople ? 'people' : 'structure'} onChange={(e) => setIncludePeople(e.target.value === 'people')}>
                  <FormControlLabel value="structure" control={<Radio size="small" />} label={<Typography variant="body2">Import structure only</Typography>} />
                  <FormControlLabel value="people" control={<Radio size="small" />} label={<Typography variant="body2">Import structure + people found (saved as a roster — no logins or emails yet; add accounts later with Import members)</Typography>} />
                </RadioGroup>
              </Alert>
            )}

            {structure.divisions?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Divisions</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {structure.divisions.map((dv) => (
                    <Chip key={dv.id} label={<RenameField kind="divisions" unit={dv} bold={false} />} onDelete={() => removeDivision(dv.id)} variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {!existingOrganization && (
              <TextField
                label="Organization name"
                value={structure.orgName || ''}
                onChange={(e) => setStructure({ ...structure, orgName: e.target.value })}
                size="small"
                sx={{ flex: 2 }}
                required
              />
              )}
              <TextField
                label="Industry"
                value={structure.industry || ''}
                onChange={(e) => setStructure({ ...structure, industry: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
              />
            </Stack>

            <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 1 }}>
              {structure.sites.map((site) => (
                <Box key={site.id} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PlaceIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }} component="div">
                      <RenameField kind="sites" unit={site} variant="subtitle1" />
                      {site.divisionId && structure.divisions?.find((dv) => dv.id === site.divisionId) ? <Chip label={structure.divisions.find((dv) => dv.id === site.divisionId).name} size="small" variant="outlined" sx={{ ml: 1 }} /> : null}
                      {site.location ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}— {site.location}
                        </Typography>
                      ) : null}
                      {site.region ? <Chip label={site.region} size="small" sx={{ ml: 1 }} /> : null}
                    </Typography>
                    <Tooltip title="Remove this site (and everything in it)">
                      <IconButton size="small" onClick={() => removeSite(site.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {structure.departments
                    .filter((d) => d.siteId === site.id)
                    .map((dept) => (
                      <Box key={dept.id} sx={{ ml: 3.5, mt: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} component="div">
                            <RenameField kind="departments" unit={dept} />
                          </Typography>
                          <Tooltip title="Remove this department (and its teams)">
                            <IconButton size="small" onClick={() => removeDept(dept.id)}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Box sx={{ ml: 1, mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {structure.teams
                            .filter((t) => t.departmentId === dept.id)
                            .map((team) => (
                              <Chip
                                key={team.id}
                                label={<RenameField kind="teams" unit={team} bold={false} />}
                                size="small"
                                variant="outlined"
                                color={team.nwtType === 'crew' ? 'primary' : 'default'}
                                onDelete={() => removeTeam(team.id)}
                                title={team.nwtType === 'crew' ? 'Operating crew' : 'Team'}
                              />
                            ))}
                        </Box>
                      </Box>
                    ))}
                </Box>
              ))}
            </Box>

            {(importDoc?.ambiguities || []).length > 0 && (
              <AmbiguityList items={importDoc.ambiguities} />
            )}
          </Box>
        )}

        {phase === 'done' && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {existingOrganization ? `${existingOrganization.name} updated` : `"${structure?.orgName}" is ready`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {existingOrganization
                ? 'The new sites, departments and NWTs were added to your organization (units that already existed were kept as they are).'
                : `Your organization was created with ${counts?.s} sites, ${counts?.d} departments, and ${counts?.t} NWTs.`}
              {includePeople && structure?.people?.length ? ` ${structure.people.length} people were saved to the roster — create their logins with Import members when you are ready.` : ''}
              {' '}You can fine-tune everything in Settings → Organization.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'review' ? (
          <>
            <Button onClick={handleClose} disabled={approving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={approving || (!existingOrganization && !structure?.orgName?.trim()) || !structure?.sites?.length}
              startIcon={approving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            >
              {approving ? (existingOrganization ? 'Adding…' : 'Creating…') : existingOrganization ? 'Confirm & add to organization' : 'Confirm & Create Organization'}
            </Button>
          </>
        ) : phase === 'done' ? (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <Button onClick={handleClose} disabled={phase === 'processing'}>
            {phase === 'processing' ? 'Working…' : 'Cancel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
